import axios, { AxiosHeaders, CanceledError } from "axios";
import PaginatedResponse from "common/contracts/paginated-response";
import AttorneyHubAPIService from "common/services/api/attorney-hub-api-service";
import { AccountType } from "common/values/account-type/account-type";
import Guid from "common/values/guid/guid";
import Session from "users/session/session";
import WorkTeamTemplateQuery from "work/entities/team-template/api/request-contracts/query-work-team-template-api-request";
import UpdateWorkTeamTemplateAPIRequest from "work/entities/team-template/api/request-contracts/update-work-team-template-api-request";
import WorkTeamTemplateAPIRequest from "work/entities/team-template/api/request-contracts/work-team-template-api-request";
import WorkTeamTemplateAPIResponse from "work/entities/team-template/api/response-contracts/work-team-template-api-response";
import TeamTemplate from "work/entities/team-template/team-template";
import VendorTeamName from "work/values/vendor-team-name/vendor-team-name";
import VendorTeam from "work/values/vendor-team/vendor-team";

export default class TeamTemplateAPIService {

  private readonly authHeaders: AxiosHeaders = new AxiosHeaders();

  private authHeadersWithJson(): AxiosHeaders {
    return this.authHeaders.concat({ 'Content-Type': 'application/json' });
  }

  constructor(session: Readonly<Session>) {
    if (!session.authToken?.value)
      throw new Error("Session must have an authToken to create a TeamTemplateAPIService");
    this.authHeaders.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  async getTeamTemplates(
    query: WorkTeamTemplateQuery,
    abortController?: AbortController
  ): Promise<PaginatedResponse<TeamTemplate>> {
    const url = new URL(
      `/work/team-templates?${query.asSearchParams()}`,
      AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.authHeaders,
        signal: abortController?.signal
      });
      const teamTemplates: TeamTemplate[] = [];
      response.data.forEach((teamData: WorkTeamTemplateAPIResponse) => {
        const teamResponse = Object.assign(
          new WorkTeamTemplateAPIResponse(),
          teamData
        );
        teamTemplates.push(teamResponse.deserialize());
      });
      return PaginatedResponse.from<TeamTemplate>(
        teamTemplates,
        response.headers['x-pagination']
      );
    } catch (error) {
      if (error instanceof CanceledError)
        throw error;
      throw new TeamTemplateAPIServiceError("getTeamTemplates", error);
    }
  }

  async createTeamTemplate(
    entityId: Guid,
    team: VendorTeam,
    name: VendorTeamName,
    creatorType: AccountType
  ): Promise<TeamTemplate> {
    const url = new URL("/work/team-templates", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        new WorkTeamTemplateAPIRequest(entityId, team, name, creatorType),
        {
          headers: this.authHeadersWithJson()
        });
      const responseData = Object.assign(
        new WorkTeamTemplateAPIResponse(),
        response.data
      );
      return responseData.deserialize();
    } catch (error) {
      throw new TeamTemplateAPIServiceError("createTeamTemplate", error);
    }
  }

  async updateTeamTemplate(
    originalTemplate: TeamTemplate,
    updatedTeam: VendorTeam,
    updatedTeamName: VendorTeamName
  ): Promise<TeamTemplate> {
    try {
      const url = new URL(
        `/work/team-templates/${originalTemplate.id?.value}`,
        AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.patch(
        url.toString(),
        new UpdateWorkTeamTemplateAPIRequest(originalTemplate, updatedTeam, updatedTeamName).payload,
        {
          headers: this.authHeadersWithJson()
        });
      const result = Object.assign(
        new WorkTeamTemplateAPIResponse(),
        response.data
      );
      return result.deserialize();
    } catch (error) {
      throw new TeamTemplateAPIServiceError("updateTeamTemplate", error);
    }
  }

  async deleteTeamTemplate(id: Guid): Promise<void> {
    try {
      const url = new URL(`/work/team-templates/${id.value}`, AttorneyHubAPIService.apiBaseUrl);
      await axios.delete(url.toString(), { headers: this.authHeaders });
    } catch (error) {
      throw new TeamTemplateAPIServiceError("deleteTeamTemplate", error);
    }
  }

  async registerTeamTemplateToMarketplace(
    teamId: Guid | undefined
  ): Promise<TeamTemplate> {
    try {
      const url = new URL(
        `/work/team-templates/${teamId?.value}/register-to-marketplace`,
        AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.post(url.toString(), {}, {
        headers: this.authHeaders
      });
      const responseData = Object.assign(
        new WorkTeamTemplateAPIResponse(),
        response.data
      );
      return responseData.deserialize();
    } catch (error) {
      throw new TeamTemplateAPIServiceError("registerTeamTemplateToMarketplace", error);
    }
  }
}

export class TeamTemplateAPIServiceError extends Error {
  constructor(public method: string, public error: unknown) {
    super(`Error in TeamTemplateAPIService.${method}: ${error}`);
    this.name = "TeamTemplateAPIServiceError";
  }
}